<template>
  <span class="flex flex-wrap gap-2 text-gray-500" :class="`text-xs md:text-${textSize}`">
    <!-- If location data exists and it's an object -->
    <template v-if="locationData">
      <template v-if="Array.isArray(locationData)">
        <span
          v-for="(location, index) in locationData"
          :key="index"
          class="inline-flex items-center gap-1 px-2 py-2 rounded-full border-[1px] border-gray-200"
        >
          <MapPin class="w-3 h-3 mr-[2px]" />
          {{ location.city }}, {{ location.country }}
        </span>
      </template>
      <template v-else>
        <span
          class="inline-flex items-center gap-1 px-2 rounded-full border-[1px] border-gray-200"
        >
          <MapPin class="w-3 h-3 mr-[2px]" />
          {{ locationData.city }}, {{ locationData.country }}
        </span>
      </template>
    </template>

    <!-- If locations array exists -->
    <template v-else-if="locations?.length">
      <span
        v-for="location in locations"
        :key="location.id"
        class="inline-flex items-center gap-1 px-2 rounded-full border-[1px] border-gray-200"
      >
        <MapPin class="w-3 h-3 mr-[2px]" />
        {{ location.location_name }}
      </span>
    </template>

    <!-- If city and country details exist -->
    <template v-else-if="cityDetails && countryDetails">
      <span
        class="inline-flex items-center gap-1 px-2 rounded-full border-[1px] border-gray-200"
      >
        <MapPin class="w-3 h-3 mr-[2px]" />
        {{ cityName }}, {{ countryName }}
      </span>
    </template>

    <!-- If only region exists -->
    <template v-else-if="region && !cityDetails && !countryDetails">
      <span
        class="inline-flex items-center gap-1 px-2 rounded-full border-[1px] border-gray-200"
      >
        <Globe2 class="w-3 h-3 mr-[2px]" />
        {{ region.replace('_', ' ') }}
      </span>
    </template>

    <!-- Remote badge -->
    <RemoteBadge v-if="isRemote" />
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { MapPin, Globe2 } from 'lucide-vue-next'
import RemoteBadge from './badges/RemoteBadge.vue'
import type { OpenRole } from '@/types/openRole'
import type { Application } from '@/types/application'

interface Props {
  data: OpenRole | Application | null
  textSize?: 'xs' | 'sm'
}

const props = withDefaults(defineProps<Props>(), {
  textSize: 'xs'
})

// Get the base data object depending on whether it's a JobApplication or OpenRole
const baseData = computed(() => {
  if (!props.data) return null
  return 'open_role_details' in props.data && props.data.open_role_details 
    ? props.data.open_role_details 
    : props.data as OpenRole
})

// Computed properties for different location scenarios
const locationData = computed(() => baseData.value?.location_data)
const locations = computed(() => baseData.value?.locations_details)
const cityDetails = computed(() => baseData.value?.city_details)
const countryDetails = computed(() => baseData.value?.country_details)
const region = computed(() => baseData.value?.region)
const isRemote = computed(() => baseData.value?.is_remote)

// Normalized display names for union types
const cityName = computed(() => {
  const city = cityDetails.value as unknown as { city_name?: string; name?: string } | null | undefined
  if (!city) return ''
  return (city.city_name ?? city.name ?? '').toString()
})

const countryName = computed(() => {
  const country = countryDetails.value as unknown as { country_name?: string; name?: string } | null | undefined
  if (!country) return ''
  return (country.country_name ?? country.name ?? '').toString()
})
</script>
